import {
  MSG_GITHUB_BUSY,
  MSG_GITHUB_TIMEOUT,
  MSG_TIMEOUT,
  MSG_UNAVAILABLE,
} from "./constants";

// Turns a raw axios/fetch error into plain, user-facing text.
//
// FastAPI's 422 validation responses put a `detail` array of Pydantic error
// objects like {"loc": ["body", "phoneNumber"], "msg": "field required"} on
// the response — surfacing `.msg` directly reads as engineer-facing jargon
// ("field required", "value is not a valid email address") with no mention
// of which field. This humanizes both the field name and the message.

const FIELD_NAME_OVERRIDES = {
  otp: "OTP",
  url: "URL",
  id: "ID",
  api_key: "API key",
};

function humanizeFieldName(loc) {
  if (!Array.isArray(loc) || loc.length === 0) return "";
  // Pydantic locs look like ["body", "phoneNumber"] or ["body", "items", 0, "email"] —
  // the last string segment is the actual field name.
  const field = [...loc].reverse().find((part) => typeof part === "string" && part !== "body");
  if (!field) return "";
  const override = FIELD_NAME_OVERRIDES[field.toLowerCase()];
  if (override) return override;
  return field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// [pattern, replacement] — replacement is a string or a (match) => string function.
const MESSAGE_TRANSLATIONS = [
  [/^field required$/i, "is required"],
  [/^none is not an allowed value$/i, "is required"],
  [/value is not a valid email address/i, "must be a valid email address"],
  [/ensure this value has at least (\d+) characters?/i, (m) => `must be at least ${m[1]} characters`],
  [/ensure this value has at most (\d+) characters?/i, (m) => `must be at most ${m[1]} characters`],
  [/string should have at least (\d+) characters?/i, (m) => `must be at least ${m[1]} characters`],
  [/string should have at most (\d+) characters?/i, (m) => `must be at most ${m[1]} characters`],
  [/input should be a valid (\w+)/i, (m) => `must be a valid ${m[1]}`],
  [/value is not a valid (\w+)/i, (m) => `must be a valid ${m[1]}`],
  [/string does not match regex.*/i, "is not in the correct format"],
  [/ensure this value is greater than or equal to (-?\d+)/i, (m) => `must be at least ${m[1]}`],
  [/ensure this value is less than or equal to (-?\d+)/i, (m) => `must be at most ${m[1]}`],
];

function translateMessage(rawMsg) {
  if (!rawMsg) return "is invalid";
  for (const [pattern, replacement] of MESSAGE_TRANSLATIONS) {
    const match = rawMsg.match(pattern);
    if (match) {
      return typeof replacement === "function" ? replacement(match) : replacement;
    }
  }
  return "Check this field and try again.";
}

function humanizeValidationError(item) {
  if (typeof item === "string") return item;
  const field = humanizeFieldName(item?.loc);
  const message = translateMessage(item?.msg);
  return field ? `${field} ${message}.` : `${message}.`;
}

/**
 * Machine code from auth Lambda / FastAPI OTP error_response: errors[0].code.
 * Also reads AuthServiceError.code when the axios error was already wrapped.
 * Does not prefer axios transport codes (ERR_NETWORK, ECONNABORTED) over body codes.
 */
export function getApiErrorCode(error) {
  if (!error) return "";

  const bodyErrors = error.response?.data?.errors;
  if (Array.isArray(bodyErrors) && bodyErrors.length > 0) {
    const code = bodyErrors[0]?.code;
    if (typeof code === "string" && code.trim()) return code.trim();
  }

  // FastAPI HTTPException with structured detail.errors (legacy shape).
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const detailErrors = detail.errors;
    if (Array.isArray(detailErrors) && detailErrors.length > 0) {
      const code = detailErrors[0]?.code;
      if (typeof code === "string" && code.trim()) return code.trim();
    }
    if (typeof detail.code === "string" && detail.code.trim()) {
      return detail.code.trim();
    }
  }

  const AXIOS_TRANSPORT = new Set([
    "ERR_NETWORK",
    "ECONNABORTED",
    "ERR_CANCELED",
    "ETIMEDOUT",
    "ECONNREFUSED",
    "ERR_BAD_RESPONSE",
  ]);
  if (
    typeof error.code === "string" &&
    error.code.trim() &&
    !AXIOS_TRANSPORT.has(error.code)
  ) {
    return error.code.trim();
  }

  return "";
}

/**
 * Extra top-level fields from auth Lambda error_response (resume payload, etc.).
 * Prefers AuthServiceError.extras when the axios error was already wrapped.
 */
export function getApiErrorExtras(error) {
  if (error?.extras && typeof error.extras === "object" && !Array.isArray(error.extras)) {
    return error.extras;
  }
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return {};
  const {
    success: _success,
    message: _message,
    errors: _errors,
    detail: _detail,
    statusCode: _statusCode,
    actions: _actions,
    ...extras
  } = data;
  return extras;
}

/**
 * @param {*} error - an axios error, AuthServiceError, or anything error-shaped
 * @param {string} fallback - shown when nothing usable can be extracted
 */
export function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  if (!error) return fallback;

  // cog_authService's AuthServiceError already resolved its message from the
  // raw axios error the first time this ran (in toAuthServiceError). It has
  // no `.response` of its own, so re-inspecting `.response.data` below would
  // always miss and fall through to the generic "Unable to connect" text —
  // discarding a perfectly good, already-humanized message.
  if (error.name === "AuthServiceError" && typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }

  // AuthServiceError (and similar wraps) already hold the API `message` — do not
  // treat missing `.response` as a network failure.
  if (
    error.name === "AuthServiceError" &&
    typeof error.message === "string" &&
    error.message.trim()
  ) {
    return error.message.trim();
  }

  // No HTTP response: true network/CORS failure for axios; otherwise use Error.message.
  if (!error.response) {
    if (
      typeof error.message === "string" &&
      error.message.trim() &&
      !error.isAxiosError &&
      !error.request
    ) {
      return error.message.trim();
    }
    const requestUrl = error.config?.url || "";
    if (String(requestUrl).includes("/github")) {
      const timedOut =
        error.code === "ECONNABORTED" ||
        error.code === "ETIMEDOUT" ||
        /timeout/i.test(String(error.message || ""));
      return timedOut ? MSG_GITHUB_TIMEOUT : MSG_GITHUB_BUSY;
    }
    return "Unable to connect. Check your internet connection and try again.";
  }

  const responseData = error.response?.data;
  const status = error.response?.status;
  const requestUrl = error.config?.url || "";
  const looksLikeGatewayPage = (value) => {
    const text = typeof value === "string" ? value : "";
    return /<html/i.test(text) || /502 Bad Gateway|504 Gateway/i.test(text);
  };
  if (looksLikeGatewayPage(responseData) || looksLikeGatewayPage(responseData?.detail)) {
    const github = String(requestUrl).includes("/github");
    if (status === 408 || status === 504) {
      return github ? MSG_GITHUB_TIMEOUT : MSG_TIMEOUT;
    }
    return github ? MSG_GITHUB_BUSY : MSG_UNAVAILABLE;
  }

  const detail = error.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map(humanizeValidationError).join(" ");
  }

  const message = error.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;

  // Prefer errors[0].message from the Lambda error_response shape.
  const errors = error.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const errMsg = errors[0]?.message;
    if (typeof errMsg === "string" && errMsg.trim()) return errMsg.trim();
  }

  if (typeof error.message === "string" && error.message.trim() && error.message !== "Request failed with status code 409") {
    // Avoid axios's generic status-code message when body had nothing useful.
    const axiosGeneric = /^Request failed with status code \d+$/i.test(error.message);
    if (!axiosGeneric) return error.message.trim();
  }

  return fallback;
}
